import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { capitalize } from 'lodash'

// hixme-ui
import Container from '@hixme-ui/container'
import Currency from '@hixme-ui/currency'
import Text from '@hixme-ui/text'
import { FormatDate } from '@hixme-ui/dates'

import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard'
import {
  maskCardNumber,
  formatPanDisplay,
  formatVcnExpiration,
} from 'helpers/virtual-card'
import NoCard from './NoCard'
import GeneratorLoader from './GeneratorLoader'
import CardBody from './CardBody'

// Extracts date string (YYYY-MM-DD) from ISO date string
// example input: '2030-12-31T23:59:59Z' -> '2030-12-31'
export const getDateString = (isoDateString) => isoDateString?.split('T')[0]

// example input: '2030-12-31T23:59:59Z' -> '12/2030'; invalid/missing -> 'INVALID DATE'
export const formatExpirationDate = (expirationDate) =>
  formatVcnExpiration(expirationDate, 'INVALID DATE')

const generateCardBody = (card, person = {}) => {
  const [showCardNumber, setShowCardNumber] = useState(false)
  const cardNumber = showCardNumber
    ? formatPanDisplay(card.pan)
    : maskCardNumber(card.pan)
  const cardHolder =
    card.firstName && card.lastName
      ? `${card.firstName} ${card.lastName}`
      : (card.cardholderName ?? person.FullName)

  const formattedExpirationDate = formatVcnExpiration(
    card.expiration_time,
    'INVALID DATE'
  )

  return [
    {
      key: 'Cardholder name',
      value: (
        <CopyToClipboard text={cardHolder}>
          <Text fontWeight="600">{cardHolder}</Text>
        </CopyToClipboard>
      ),
    },
    {
      key: 'Card Number',
      value: (
        <div>
          <CopyToClipboard text={card.pan}>
            <Text fontWeight="600">{cardNumber}</Text>
          </CopyToClipboard>
          <Text
            small
            primary
            onClick={() => setShowCardNumber(!showCardNumber)}
            cursor="pointer"
          >
            {showCardNumber ? 'Hide' : 'Show'}
          </Text>
        </div>
      ),
    },
    {
      key: 'VCN Provider',
      value: <Text fontWeight="600">{card.vcnProvider}</Text>,
    },
    {
      key: 'Expiration Date',
      value: (
        <CopyToClipboard text={formattedExpirationDate}>
          <Text fontWeight="600">{formattedExpirationDate}</Text>
        </CopyToClipboard>
      ),
    },
    {
      key: 'CVV',
      value: (
        <CopyToClipboard text={card.cvv_number}>
          <Text fontWeight="600">{card.cvv_number}</Text>
        </CopyToClipboard>
      ),
    },
    {
      key: 'Monthly card limit',
      value:
        card.monthly_card_limit === '-' ? (
          <Text fontWeight="600">-</Text>
        ) : (
          <Currency fontWeight="600">{card.monthly_card_limit}</Currency>
        ),
    },
    {
      key: 'Card status',
      value: <Text fontWeight="600">{capitalize(card.state)}</Text>,
    },
    {
      key: 'Created/Last updated',
      value: (
        <Text>
          <FormatDate fontWeight="600" date={card.created_time} /> /{' '}
          <FormatDate fontWeight="600" date={card.last_modified_time} />
        </Text>
      ),
    },
    {
      key: 'TokenID',
      value: <Text fontWeight="600">{card.token ?? card.merchantLogId}</Text>,
    },
  ]
}

const VirtualCard = ({
  benefitId,
  card,
  onGenerate,
  isGenerating,
  slim,
  person,
  isCreateAvailable,
  currentHealthPlanId,
}) => (
  <Container flex alignCenter noPadding>
    {card.token ? (
      <Container flex noPadding alignCenter width="100%">
        {isGenerating && <GeneratorLoader />}
        {!isGenerating && (
          <CardBody data={generateCardBody(card, person)} slim={slim} />
        )}
      </Container>
    ) : (
      <NoCard
        benefitId={benefitId}
        employeePublicKey={person.EmployeePublicKey}
        onGenerateCard={onGenerate}
        isCreateAvailable={isCreateAvailable}
        currentHealthPlanId={currentHealthPlanId}
      />
    )}
  </Container>
)

VirtualCard.propTypes = {
  benefitId: PropTypes.string.isRequired,
  currentHealthPlanId: PropTypes.string.isRequired,
  card: PropTypes.shape({
    id: PropTypes.string,
  }),
  person: PropTypes.object.isRequired,
  onGenerate: PropTypes.func.isRequired,
  isCreateAvailable: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool,
  slim: PropTypes.bool,
}

VirtualCard.defaultProps = {
  card: {},
  slim: false,
  isGenerating: false,
}

export default VirtualCard
